import { createContext, PropsWithChildren, useContext, useState } from 'react';

import { WithSetStateActions } from '@core';

import type { IntrinsicCameraParams } from './utils/rotationUtils';

// Camera mode: 'orbit' for free movement, 'intrinsic' for locked angle visualization
export type CameraMode = 'orbit' | 'intrinsic';

type ElvaVisualizerState = {
    currentFocusId: string | null;
    forcedAzimuthalAngle: number | null;
    forcedPolarAngle: number | null;
    cameraMode: CameraMode;
    intrinsicCameraParams: IntrinsicCameraParams | null;
};

const noop = () => {};

const ElvaVisualizerContext = createContext<WithSetStateActions<ElvaVisualizerState>>({
    currentFocusId: null,
    setCurrentFocusId: noop,
    forcedAzimuthalAngle: null,
    setForcedAzimuthalAngle: noop,
    forcedPolarAngle: null,
    setForcedPolarAngle: noop,
    cameraMode: 'orbit',
    setCameraMode: noop,
    intrinsicCameraParams: null,
    setIntrinsicCameraParams: noop,
});

export const useElvaVisualizerContext = () => useContext(ElvaVisualizerContext);

export const ElvaVisualizerProvider = ({ children }: PropsWithChildren) => {
    const [currentFocusId, setCurrentFocusId] = useState<string | null>(null);
    const [forcedAzimuthalAngle, setForcedAzimuthalAngle] = useState<number | null>(null);
    const [forcedPolarAngle, setForcedPolarAngle] = useState<number | null>(null);
    const [cameraMode, setCameraMode] = useState<CameraMode>('orbit');
    const [intrinsicCameraParams, setIntrinsicCameraParams] = useState<IntrinsicCameraParams | null>(null);

    return (
        <ElvaVisualizerContext.Provider
            value={{
                currentFocusId,
                setCurrentFocusId,
                forcedAzimuthalAngle,
                setForcedAzimuthalAngle,
                forcedPolarAngle,
                setForcedPolarAngle,
                cameraMode,
                setCameraMode,
                intrinsicCameraParams,
                setIntrinsicCameraParams,
            }}
        >
            {children}
        </ElvaVisualizerContext.Provider>
    );
};
